import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../../hooks";
import { updateEmployee } from "../../../slices/employeeSlice";
import type { EmployeeItem } from "../../../types/employee";
import { toast } from "react-toastify";

interface UpdateEmployeeBasicInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: EmployeeItem;
}

const UpdateEmployeeBasicInfoModal: React.FC<UpdateEmployeeBasicInfoModalProps> = ({
  isOpen,
  onClose,
  employee,
}) => {
  const dispatch = useAppDispatch();

  if (!isOpen) return null;

  // ✅ Validation Schema
  const validationSchema = Yup.object({
    firstname: Yup.string().required("Firstname is required"),
    lastname: Yup.string().required("Lastname is required"),
    email: Yup.string().email("Must be a valid email address format").required("Email address is required"),
    phoneNumber: Yup.string().required("Phonr number is required"),
    othername: Yup.string().nullable(),
  });

  // ✅ Handle submit
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      console.log("Payload: ", values);
      console.log("User ID: ", employee.id);
      await dispatch(updateEmployee({ id: employee.id, data: values })).unwrap();
      toast.success("Updated");
      onClose();
    } catch (err) {
      console.error("Failed to update employee:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[999]">
      <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-md">
        <h3 className="text-lg text-left font-semibold text-gray-800 mb-4">
          Edit Basic Information
        </h3>

        <Formik
          initialValues={{
            firstname: employee.firstname || "",
            lastname: employee.lastname || "",
            othername: employee.othername || "",
            email: employee.email || "",
            phoneNumber: employee.phoneNumber || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="space-y-4">
              {/* Firstname */}
              <div>
                <label className="form-label">Firstname</label>
                <Field
                  name="firstname"
                  className="form-input mt-1 w-full"
                  placeholder="Enter firstname"
                />
                {errors.firstname && touched.firstname && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.firstname}
                  </p>
                )}
              </div>

              <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                {/* Othername */}
                <div>
                  <label className="form-label">Othername(s)</label>
                  <Field
                    name="othername"
                    className="form-input mt-1 w-full"
                    placeholder="Enter other name(s)"
                  />
                </div>

                {/* Lastname */}
                <div>
                  <label className="form-label">Lastname</label>
                  <Field
                    name="lastname"
                    className="form-input mt-1 w-full"
                    placeholder="Enter lastname"
                  />
                  {errors.lastname && touched.lastname && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.lastname}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-2 grid-cols-1">
                {/* Email */}
                <div>
                  <label className="form-label">Email</label>
                  <Field
                    name="email"
                    className="form-input mt-1 w-full"
                    placeholder="Enter email"
                  />
                  {errors.email && touched.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="form-label">Phone number</label>
                  <Field
                    name="phoneNumber"
                    className="form-input mt-1 w-full"
                    placeholder="Enter phone number"
                  />
                  {errors.phoneNumber && touched.phoneNumber && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="modal-btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default UpdateEmployeeBasicInfoModal;
